import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { fastApiURL } from '../../utils/config';

const InvitationPage = ({ action }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. No token provided.');
      setLoading(false);
      return;
    }

    handleInvitation();
  }, [token]);

  const handleInvitation = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const endpoint = action === 'accept' 
        ? `${fastApiURL}/api/auth/organizations/invitations/accept`
        : `${fastApiURL}/api/auth/organizations/invitations/deny`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Failed to ${action} invitation`;
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setSuccess(result.message || `Invitation ${action === 'accept' ? 'accepted' : 'declined'} successfully!`);
    } catch (err) {
      setError(err.message || `Failed to ${action} invitation`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Organization Invitation
        </Typography>

        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" my={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {action === 'accept' ? 'Processing your acceptance...' : 'Processing your decline...'}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </Box>
          </>
        )}

        {!loading && !error && !success && (
          <Typography variant="body1" align="center" color="text.secondary">
            Processing invitation...
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default InvitationPage;
